/* eslint-disable max-lines */
import React, { Fragment, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import moment from 'moment';
import classNames from 'classnames';
import TocIcon from '@material-ui/icons/Toc';
import { isMobile } from 'react-device-detect';

import logger from 'logger';
import { translate } from 'services/i18nService';
import { OPEN_TIP_MENU_DRAWER } from 'services/MessagingService/EventNames';
import createMessagingService from 'services/MessagingService/MessagingService';
import ModalService from 'services/ModalService';
import SessionStorageService from 'services/SessionStorageService';
import GamesStore from 'stores/GamesStore';
import GroupShowStore from 'stores/GroupShowStore';
import ProfileStore from 'stores/ProfileStore';
import FirebaseStore from 'stores/FirebaseStore';
import UserStore from 'stores/UserStore';
import { BROADCAST_STATUS, NEW_SHOWS_STATE } from 'enum/videoPlayer';
import allUserGroups from 'enum/userGroups';
import { GAMES_STATUS, GAMES } from 'utils/consts';
import { chatFontSettings, isMyProfileHelperFunc } from 'utils/helpers';

import tokensImage from '../../../images/tokens-icon-dark.svg';
import coinImageTier3 from '../../../images/chat/big-hand.png';

import styles from './ChatNotificationsTypes.css';


const stopPropagation = (e) => e.stopPropagation();

// eslint-disable-next-line complexity
function GameMobileNotification(props) {
  const {
    chatSettings,
    embedProfileV2,
    handleTabsChange,
    isBroadcasterChat,
    isDesktop,
    message,
    roomName,
    setShowRegistrationModal,
    showBuyTokensModal,
    toggleGameDrawer,
  } = props;

  const MessagingService = createMessagingService();

  const {
    activeGame,
    gameName,
    gamesInfoTime,
    rollTheDiceInfo,
    rollTheDiceInfoEmpty,
    setRollTheDiceInfo,
    tipMenuGameActive,
  } = GamesStore;

  const { isGuestUser, user } = UserStore;

  const {
    broadcastStatus,
    profileName,
  } = ProfileStore;

  const { newShowsState } = FirebaseStore;

  const parsedNotifExtra = JSON.parse(message.notificationExtra);

  const isGameResumed = Object.keys(parsedNotifExtra).length === 1;
  const showResultOfGame = Object.hasOwn(parsedNotifExtra, 'r');
  const isThereRollTheDiceInfo = Object.hasOwn(parsedNotifExtra, 'o') && message.gameName === GAMES.ROLL_THE_DICE;
  const isGameInActiveState = Object.keys(parsedNotifExtra).length > 0;
  const isBroadcasterAsViewer = (
    UserStore.hasCF(allUserGroups.BROADCASTER_AS_VIEWER) &&
    isMyProfileHelperFunc(user, profileName)
  );

  const resumedGameClass = classNames({
    [styles.gameResumedDesktop]: isDesktop,
    [styles.gameResumed]: !isDesktop,
    [styles.clickOnResumedGame]: (
      isDesktop &&
      !isGameResumed &&
      parsedNotifExtra.s === GAMES_STATUS.IDLE
    ),
  });

  const gameEndClass = classNames({
    [styles.gameEndedDesktop]: isDesktop,
    [styles.gameEnded]: !isDesktop,
  });

  const isOldPrize = moment(gamesInfoTime).diff(message.timestamp, 'seconds') > 5;

  useEffect(() => {
    if (isThereRollTheDiceInfo && rollTheDiceInfoEmpty) {
      setRollTheDiceInfo(parsedNotifExtra);
    }
  }, [
    isThereRollTheDiceInfo,
    parsedNotifExtra,
    rollTheDiceInfoEmpty,
    setRollTheDiceInfo,
  ]);

  const handleGamesPicture = (currentGameName) => {
    switch (currentGameName) {
      case GAMES.ROLL_THE_DICE:
        return styles.rolltheDice;

      case GAMES.TOKEN_SQUARES:
        return styles.tokenKeno;

      case GAMES.SPIN_THE_WHEEL:
        return styles.spinTheWheel;

      default:
        return styles.defaultStyle;
    }
  };

  const handleTranslatedRollNumber = () => {
    const number = parsedNotifExtra.r;
    const translatedOptions = [
      'v2profile.v2.one',
      'v2profile.v2.two',
      'v2profile.v2.three',
      'v2profile.v2.four',
      'v2profile.v2.five',
      'v2profile.v2.six',
    ];

    return translate(translatedOptions[number - 1]);
  };

  const handleSpinTheWheelResult = () => `${parsedNotifExtra.o[parsedNotifExtra.r].o}`;

  const handleKenoResult = () => parsedNotifExtra.r;

  const handleRolledOption = () => {
    const rollOptionName =
      (Object.keys(rollTheDiceInfo).length > 0 && isOldPrize && rollTheDiceInfo.o[parsedNotifExtra.r]) ||
      (activeGame && activeGame.details.o[parsedNotifExtra.r]) ||
      '';

    return rollOptionName;
  };

  // eslint-disable-next-line complexity
  const renderDescriptionText = (currentGameName) => {
    const {
      anonymous,
      isGuest,
      senderOriginalUsername,
    } = message || {};

    let username = undefined;
    if (isGuest === '1') {
      username = translate('v2profile.gift.sender.guest');
    } else if (anonymous === true || anonymous === '1') {
      username = translate('v2profile.gift.sender.anonymous');
    } else {
      username = senderOriginalUsername;
    }

    const isIdle = parsedNotifExtra.s === GAMES_STATUS.IDLE;

    let diceResult = undefined;
    let kenoResult = undefined;
    let wheelResult = undefined;
    switch (currentGameName) {
      case GAMES.ROLL_THE_DICE:
        if (isIdle) {
          if (isDesktop) {
            return (
              <Fragment>
                <span>
                  {translate(
                    'v2profile.v2.token.keno.desktop',
                    { 0: 6, 1: roomName }
                  )}
                </span>
                {!isBroadcasterChat && !isBroadcasterAsViewer && (
                  <span className={styles.clickOn}>
                    {translate('v2profile.click.to.play')}
                  </span>
                )}
              </Fragment>
            );
          }

          return translate(
            'v2profile.v2.roll.the.dice.description',
            { 0: roomName }
          );
        }

        if (isDesktop) {
          diceResult = `<span class=${styles.yellowBlink}>'${handleTranslatedRollNumber()}'<span>`;
        } else {
          diceResult = `'${handleTranslatedRollNumber()}'`;
        }

        return translate('v2profile.v2.roll.the.dice.rolled', {
          0: username,
          1: diceResult,
          2: handleRolledOption(),
        });

      case GAMES.TOKEN_SQUARES:
        if (isIdle) {
          if (isDesktop) {
            return (
              <Fragment>
                <span>
                  {translate(
                    'v2profile.v2.token.keno.description',
                    { 0: roomName }
                  )}
                </span>
                {!isBroadcasterChat && !isBroadcasterAsViewer && (
                  <span className={styles.clickOn}>
                    {translate('v2profile.click.to.play')}
                  </span>
                )}
              </Fragment>
            );
          }

          return translate(
            'v2profile.v2.token.keno.description',
            { 0: roomName }
          );
        }

        if (isDesktop) {
          kenoResult = `<span class=${styles.yellowBlink}>${handleKenoResult()}</span>`;
        } else {
          kenoResult = handleKenoResult();
        }

        return translate('v2profile.v2.token.keno.result', {
          0: username,
          1: kenoResult,
        });

      case GAMES.SPIN_THE_WHEEL:
        if (isIdle) {
          if (isDesktop) {
            return (
              <Fragment>
                <span>
                  {translate(
                    'v2profile.spin.the.wheel.click.on',
                    { 0: roomName }
                  )}
                </span>
                {!isBroadcasterChat && !isBroadcasterAsViewer && (
                  <span className={styles.clickOn}>
                    {translate('v2profile.click.to.play')}
                  </span>
                )}
              </Fragment>
            );
          }

          return translate(
            'v2profile.v2.spin.the.wheel.description',
            { 0: roomName }
          );
        }

        if (isDesktop) {
          wheelResult = `<span class=${styles.yellowBlink}>${handleSpinTheWheelResult()}</span>`;
        } else {
          wheelResult = handleSpinTheWheelResult();
        }

        return translate('v2profile.v2.spin.the.wheel.result', {
          0: username,
          1: wheelResult,
        });

      default:
        logger.error(`Unexpected game active: ${currentGameName}`);

        return null;
    }
  };

  const onOpenTipMenu = useCallback((e) => {
    e.stopPropagation();
    if (isBroadcasterChat || isBroadcasterAsViewer) {
      return;
    }
    if (isDesktop) {
      ModalService.showModal(
        ModalService.MODAL_KEYS.TIP_MENU_MODAL,
        { roomName, embedProfileV2, setShowRegistrationModal }
      );
    } else {
      MessagingService.emit(OPEN_TIP_MENU_DRAWER);
    }
  }, [
    MessagingService,
    embedProfileV2,
    isBroadcasterAsViewer,
    isBroadcasterChat,
    isDesktop,
    roomName,
    setShowRegistrationModal,
  ]);

  const determineGameTabIndex = useCallback(() => {
    const showPublicChatCF = UserStore.hasCF(allUserGroups.PROFILES_V2_SPECIAL_SHOW_PUBLIC_CHAT);
    const showGSchat = (
      showPublicChatCF &&
      GroupShowStore.userHaveTicket(user.username) &&
      broadcastStatus === BROADCAST_STATUS.INSIDE_GS &&
      newShowsState === NEW_SHOWS_STATE.INSIDE_GS
    );

    if (!embedProfileV2) {
      return showGSchat ? 5 : 4;
    }

    if (SessionStorageService.get('wasUserInPrivateShow')) {
      return showGSchat ? 3 : 2;
    }

    return showGSchat ? 2 : 1;
  }, [
    broadcastStatus,
    embedProfileV2,
    newShowsState,
    user.username,
  ]);

  const onShowBuyTokensModal = useCallback(() => {
    if (embedProfileV2 && !user.username) {
      setShowRegistrationModal(true);
    } else {
      showBuyTokensModal();
    }
  }, [embedProfileV2, showBuyTokensModal, setShowRegistrationModal, user.username]);

  const openGame = useCallback((e) => {
    e.stopPropagation();
    if (isBroadcasterChat || isBroadcasterAsViewer) {
      return;
    }

    if (
      isGameInActiveState &&
      !isGameResumed &&
      gameName &&
      parsedNotifExtra.s === GAMES_STATUS.IDLE
    ) {
      const gameTabIndex = determineGameTabIndex();
      if (isDesktop) {
        if (isGuestUser) {
          onShowBuyTokensModal();
        } else {
          handleTabsChange(gameTabIndex);
        }
      } else if (!isDesktop && isGuestUser) {
        onShowBuyTokensModal();
      } else {
        toggleGameDrawer();
      }
    }
  }, [
    determineGameTabIndex,
    gameName,
    handleTabsChange,
    isBroadcasterAsViewer,
    isBroadcasterChat,
    isDesktop,
    isGameInActiveState,
    isGameResumed,
    isGuestUser,
    onShowBuyTokensModal,
    parsedNotifExtra,
    toggleGameDrawer,
  ]);

  if (message.gameName === 'TippingBar') {
    return null;
  }

  const translatedName = () => {
    switch (message.gameName) {
      case GAMES.TOKEN_SQUARES:
        return translate('tipping.tipping.games.game.tokensquares.ui.game.name');

      case GAMES.ROLL_THE_DICE:
        return translate('tipping.tipping.games.game.rollthedice.ui.game.name');

      case GAMES.SPIN_THE_WHEEL:
        return translate('tipping.tipping.games.game.wheel.ui.game.name');

      default:
        return undefined;
    }
  };

  const tocIcon = classNames({
    [styles.tocIcon]: true,
    [styles.broadcasterTocIcon]: isBroadcasterChat,
  });

  const finalGameName = message.gameName === 'TokenSquares' ? 'TokenKeno' : message.gameName;

  if (message.gameName === GAMES.TIP_MENU) {
    let username = undefined;
    const isAnonymous = message.anonymous === true || message.anonymous === '1';
    if (message.isGuest === '1') {
      username = translate('v2profile.gift.sender.guest');
    } else if (isAnonymous) {
      username = translate('v2profile.gift.sender.anonymous');
    } else {
      username = message.senderOriginalUsername;
    }

    const rowsToShow = isMobile ? 2 : 4;
    if (parsedNotifExtra.s === GAMES_STATUS.IDLE && tipMenuGameActive) {
      return (
        <div className={styles.tipMenuNotif} onClick={onOpenTipMenu}>
          {parsedNotifExtra.o.slice(0, rowsToShow).map((item) => (
            <div key={item.pr} className={styles.tipMenuRow}>
              <div>{item.pr}</div>
              <div className={styles.price}>
                <img alt="tokens" src={tokensImage} />
                {' '}
                <div>{item.p}</div>
              </div>
            </div>
          ))}
          {!isBroadcasterChat && !isBroadcasterAsViewer && (
            <div className={styles.seeTipMenu} onClick={onOpenTipMenu}>
              {translate('v2profile.see.tip.menu')}
            </div>
          )}
        </div>
      );
    }

    if (Object.hasOwn(parsedNotifExtra, 'i')) {
      return (
        <div className={styles.tipMenuTip} onClick={onOpenTipMenu}>
          <img alt="coin" src={coinImageTier3} />
          {translate('v2profile.tip.menu.tip.notif', {
            0: `<strong>${username}</strong>`,
            1: `<strong>${parsedNotifExtra.p}</strong>`,
            2: '<strong>',
            3: '</strong>',
            4: `<strong>'${parsedNotifExtra.pr}'</strong>`,
          })}
          <TocIcon className={tocIcon} />
        </div>
      );
    }

    return null;
  }

  if (isGameInActiveState) {
    const isResumed = (
      isGameResumed ||
      showResultOfGame ||
      (parsedNotifExtra.s === GAMES_STATUS.IDLE && isDesktop)
    );

    const wrapperClassName = isResumed ? resumedGameClass : styles.initGamesWrap;

    return (
      <div
        className={wrapperClassName}
        onClick={openGame}
      >
        <div className={handleGamesPicture(message.gameName)} />
        {isGameResumed ? (
          <div
            className={styles.gamesDesc}
            style={chatFontSettings(isDesktop, chatSettings)}
          >
            {translate(
              'v2profile.v2.game.resumed',
              { 0: translatedName() }
            )}
          </div>
        ) : (
          <div
            className={styles.gamesDesc}
            style={chatFontSettings(isDesktop, chatSettings)}
          >
            {renderDescriptionText(message.gameName)}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={gameEndClass}
      style={chatFontSettings(isDesktop, chatSettings)}
      onClick={stopPropagation}
    >
      {translate(
        'v2profile.v2.game.ended',
        { 0: finalGameName }
      )}
    </div>
  );
}

GameMobileNotification.propTypes = {
  chatSettings: PropTypes.object,
  embedProfileV2: PropTypes.bool,
  handleTabsChange: PropTypes.func,
  isBroadcasterChat: PropTypes.bool,
  isDesktop: PropTypes.bool,
  message: PropTypes.object,
  roomName: PropTypes.string,
  setShowRegistrationModal: PropTypes.func,
  showBuyTokensModal: PropTypes.func,
  toggleGameDrawer: PropTypes.func,
};

GameMobileNotification.defaultProps = {
  chatSettings: undefined,
  embedProfileV2: false,
  handleTabsChange: undefined,
  isBroadcasterChat: undefined,
  isDesktop: undefined,
  message: undefined,
  roomName: undefined,
  setShowRegistrationModal: () => undefined,
  showBuyTokensModal: undefined,
  toggleGameDrawer: undefined,
};

export default observer(GameMobileNotification);
